import type { PageFlagKey } from '@/shared/types/passbook';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';
import type {
  AiSettings,
  AiSettingsPatch,
  AiUserRow,
  EffectiveAiSettings,
  PagedResult,
} from '@/features/ai-credits/model/types';

interface Envelope<T> {
  data: T;
}

/** Flags de página do passbook que o master liga/desliga (allowlist do legado). */
export type PassbookFlagKey = PageFlagKey;

export interface PassbookPermissionRow {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  /** Cor de fundo do cartão (`passbooks.backgroundColor`). */
  backgroundColor?: string;
  inactive: boolean;
  /** Programa de tiers ligado (Recursos do programa). Sem ele, as páginas de tiers somem de Páginas. */
  hasTiers: boolean;
  flags: Record<PassbookFlagKey, boolean>;
  aiSettings: AiSettings;
  storeCount: number;
}

export interface StorePermissionRow {
  id: string;
  storeCode: string;
  name: string;
  city: string;
  aiSettings: AiSettings;
  effective: EffectiveAiSettings;
}

export const PERMISSIONS_PAGE_SIZE = 20;

export const permissionKeys = {
  all: ['permissions'] as const,
  passbooks: (search: string, page: number) => ['permissions', 'passbooks', search, page] as const,
  passbook: (id: string) => ['permissions', 'passbook', id] as const,
};

function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export function usePermissionPassbooks(search: string, page: number) {
  return useQuery({
    queryKey: permissionKeys.passbooks(search, page),
    queryFn: async () =>
      (
        await legacy.get<Envelope<PagedResult<PassbookPermissionRow>>>(
          `/admin/permissions/passbooks${toQuery({ search, page, limit: PERMISSIONS_PAGE_SIZE })}`,
        )
      ).data,
    placeholderData: keepPreviousData,
  });
}

export function usePermissionPassbook(id: string | null) {
  return useQuery({
    queryKey: permissionKeys.passbook(id ?? ''),
    queryFn: async () =>
      (await legacy.get<Envelope<PassbookPermissionRow>>(`/admin/permissions/passbooks/${id}`)).data,
    enabled: Boolean(id),
  });
}

/**
 * Atualização otimista: o Switch responde na hora e volta se o legado recusar.
 * Também atualiza a linha na lista da esquerda sem refetch.
 */
export function useUpdatePassbook(id: string) {
  const queryClient = useQueryClient();
  const key = permissionKeys.passbook(id);

  return useMutation({
    mutationFn: async (input: { flags?: Partial<Record<PassbookFlagKey, boolean>>; aiSettings?: AiSettingsPatch }) =>
      (await legacy.put<Envelope<PassbookPermissionRow>>(`/admin/permissions/passbooks/${id}`, input)).data,
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PassbookPermissionRow>(key);
      if (previous) {
        queryClient.setQueryData<PassbookPermissionRow>(key, {
          ...previous,
          flags: { ...previous.flags, ...input.flags },
          aiSettings: { ...previous.aiSettings, ...input.aiSettings },
        });
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (row, input) => {
      queryClient.setQueryData(key, row);
      queryClient.setQueriesData<PagedResult<PassbookPermissionRow>>(
        { queryKey: ['permissions', 'passbooks'] },
        (page) => page && { ...page, rows: page.rows.map((r) => (r.id === row.id ? row : r)) },
      );
      if (input.aiSettings) {
        void queryClient.invalidateQueries({ queryKey: ['permissions', 'stores', id] });
        void queryClient.invalidateQueries({ queryKey: ['permissions', 'users', id] });
      }
    },
  });
}

export async function fetchPassbookStores(
  idPassbook: string,
  params: { page: number; limit: number; search?: string },
): Promise<PagedResult<StorePermissionRow>> {
  const res = await legacy.get<Envelope<PagedResult<StorePermissionRow>>>(
    `/admin/permissions/passbooks/${idPassbook}/stores${toQuery(params)}`,
  );
  return res.data;
}

export type PanelUserStatus = 'all' | 'active' | 'inactive';

export interface PassbookUsersParams {
  page: number;
  limit: number;
  search?: string;
  storeCode?: string;
  role?: string;
  status?: PanelUserStatus;
}

/** Contagens do passbook inteiro (sem os filtros da lista). */
export interface PassbookUsersSummary {
  total: number;
  active: number;
  inactive: number;
  /** Usuários com Segmentações liberadas no próprio usuário. */
  /** Usuários com alguma flag de página própria. */
  customPages: number;
}

export interface PassbookUsersResult extends PagedResult<AiUserRow> {
  summary: PassbookUsersSummary;
  /** Roles distintas do passbook, no nome literal gravado no usuário. */
  roles: string[];
}

export interface CreatePassbookUserInput {
  name: string;
  email: string;
  password: string;
  role: string;
  stores: string[];
}

export function useCreatePassbookUser(idPassbook: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePassbookUserInput) =>
      (await legacy.post<Envelope<AiUserRow>>(`/admin/permissions/passbooks/${idPassbook}/users`, body)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['permissions', 'users', idPassbook] });
    },
  });
}

export function usePassbookUsers(idPassbook: string, params: PassbookUsersParams) {
  return useQuery({
    queryKey: ['permissions', 'users', idPassbook, params],
    queryFn: async () =>
      (
        await legacy.get<Envelope<PassbookUsersResult>>(
          `/admin/permissions/passbooks/${idPassbook}/users${toQuery({
            ...params,
            status: params.status === 'all' ? undefined : params.status,
          })}`,
        )
      ).data,
    placeholderData: keepPreviousData,
  });
}

export function usePassbookStores(idPassbook: string, params: { page: number; limit: number; search?: string }) {
  return useQuery({
    queryKey: ['permissions', 'stores', idPassbook, params],
    queryFn: () => fetchPassbookStores(idPassbook, params),
    placeholderData: keepPreviousData,
  });
}

export function usePassbookStoreOptions(passbook: PassbookPermissionRow) {
  return useQuery({
    queryKey: ['permissions', 'store-options', passbook.id],
    queryFn: () => fetchPassbookStores(passbook.id, { page: 0, limit: 100 }),
    enabled: passbook.storeCount > 0,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, aiSettings }: { id: string; aiSettings: AiSettingsPatch }) =>
      (await legacy.put<Envelope<StorePermissionRow>>(`/admin/permissions/stores/${id}`, { aiSettings })).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['permissions', 'stores'] });
      // O valor efetivo dos usuários da loja muda junto.
      void queryClient.invalidateQueries({ queryKey: ['permissions', 'users'] });
    },
  });
}